import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery, type QueryKey } from '@tanstack/react-query';
import { api, type Query } from '@/lib/api';

export const PAGE_SIZES = [5, 10, 20, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

type Params = Query;

/**
 * Page-by-page navigation over the API's cursor pagination (`{ data, page: { nextCursor } }`).
 * Keeps a stack of cursors so Previous works, resets to page 1 when filters or page size change,
 * and holds the previous page on screen while the next one loads (no skeleton flash).
 */
export function usePagedList<T>(key: QueryKey, path: string, params: Params, opts: { initialPageSize?: PageSize; enabled?: boolean } = {}) {
  const [pageSize, setPageSize] = useState<PageSize>(opts.initialPageSize ?? 20);
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  useEffect(() => {
    setCursors([null]);
  }, [paramsKey, pageSize]);

  const cursor = cursors[cursors.length - 1] ?? null;
  const query = useQuery({
    queryKey: [...key, params, pageSize, cursor],
    queryFn: ({ signal }) => {
      const q: Query = { limit: pageSize };
      for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') q[k] = v;
      if (cursor) q.cursor = cursor;
      return api.list<T>(path, q, { signal });
    },
    placeholderData: keepPreviousData,
    enabled: opts.enabled,
  });

  const nextCursor = query.data?.page?.nextCursor ?? null;
  return {
    ...query,
    items: query.data?.data ?? [],
    page: cursors.length,
    pageSize,
    setPageSize: (s: PageSize) => setPageSize(s),
    hasPrev: cursors.length > 1,
    hasNext: !!nextCursor,
    next: () => nextCursor && !query.isFetching && setCursors((c) => [...c, nextCursor]),
    prev: () => setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c)),
    reset: () => setCursors([null]),
  };
}
